import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Bug, Lightbulb } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";

// In-app feedback / bug report form. Submissions land in
// `feedback_submissions` and surface in the Admin Insights Feedback inbox.
export default function FeedbackPage() {
  const navigate = useNavigate();
  const [kind, setKind] = useState("feature_request");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/feedback/mine");
        setMine(r.data?.items || []);
      } catch (_) { /* silent */ }
    })();
  }, []);

  const submit = async () => {
    if (title.trim().length < 3) {
      toast.error("Add a short title (3+ chars).");
      return;
    }
    try {
      setBusy(true);
      await api.post("/feedback", { kind, title, body });
      toast.success("Sent — thanks!");
      setTitle("");
      setBody("");
      const r = await api.get("/feedback/mine");
      setMine(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen pb-32" data-testid="feedback-page">
      <header className="px-5 pt-6 pb-3 max-w-md mx-auto w-full">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-timber-700 font-bold" data-testid="feedback-back">
          <ArrowLeft size={18} weight="bold" /> Back
        </button>
        <h1 className="font-heading text-3xl font-bold mt-3" data-testid="feedback-title">Feedback</h1>
        <p className="text-sm text-timber-700 mt-1">Found a bug? Got an idea? Tell us — we read every one.</p>
      </header>

      <main className="px-5 max-w-md mx-auto w-full space-y-4">
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            data-testid="feedback-kind-feature"
            onClick={() => setKind("feature_request")}
            className={`tactile-card p-3 text-left ${kind === "feature_request" ? "border-2 border-timber-900" : ""}`}
          >
            <Lightbulb size={20} weight="duotone" className="text-masking" />
            <p className="font-bold text-timber-900 mt-1">Feature</p>
            <p className="text-[11px] text-timber-700">An idea, an improvement</p>
          </button>
          <button
            type="button"
            data-testid="feedback-kind-bug"
            onClick={() => setKind("bug")}
            className={`tactile-card p-3 text-left ${kind === "bug" ? "border-2 border-timber-900" : ""}`}
          >
            <Bug size={20} weight="duotone" className="text-block" />
            <p className="font-bold text-timber-900 mt-1">Bug</p>
            <p className="text-[11px] text-timber-700">Something broke</p>
          </button>
        </div>

        <div>
          <label className="label-cap">Short title</label>
          <Input
            data-testid="feedback-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={kind === "bug" ? "e.g. Photos won't upload on iPhone" : "e.g. Add Decking timber type"}
            className="mt-1 bg-chalk border-2 border-stone_border"
          />
        </div>

        <div>
          <label className="label-cap">Details</label>
          <Textarea
            data-testid="feedback-body-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            placeholder={kind === "bug" ? "What were you doing? What did you expect?" : "What would this unlock for you?"}
            className="mt-1 bg-chalk border-2 border-stone_border"
          />
        </div>

        <Button
          data-testid="feedback-submit-btn"
          onClick={submit}
          disabled={busy}
          className="w-full bg-timber-900 text-masking hover:bg-timber-900 min-h-[52px] font-bold"
        >
          {busy ? "Sending…" : "Send feedback"}
        </Button>

        {mine.length > 0 && (
          <section className="mt-6" data-testid="feedback-mine">
            <p className="label-cap mb-2">Your previous submissions</p>
            <ul className="space-y-2">
              {mine.map((m) => (
                <li key={m.id} className="tactile-card p-3" data-testid={`feedback-mine-${m.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-bold text-timber-900">{m.title}</p>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${m.kind === "bug" ? "bg-block text-white" : "bg-masking text-timber-900"}`}>{m.kind}</span>
                  </div>
                  {m.body && <p className="text-xs text-timber-700 mt-1 line-clamp-2">{m.body}</p>}
                  <p className="text-[10px] text-timber-500 font-mono mt-1">{m.status} · {new Date(m.created_at).toLocaleDateString()}</p>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}
